import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PortraitIcon from '@mui/icons-material/Portrait';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, IconButton, Menu, MenuItem, styled, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import theme from '../../styles/theme';
import { obtenerEstadoJugadorEnCategoria } from '../../utils/UtilesJugador';
import { FichaJugadorPlanWrapper } from '../custom/DedicatedComponents';

export const BadgeCategoria = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'estadoJugador',
})(({ theme, estadoJugador }) => {
  let backgroundColor = 'transparent';
  let color = theme.palette.text.secondary;

  if (estadoJugador === 3) {
    backgroundColor = theme.palette.categoria.plus3;
    color = '#000';
  } else if (estadoJugador === 2) {
    backgroundColor = theme.palette.categoria.plus2;
    color = '#000';
  } else if (estadoJugador === 1) {
    backgroundColor = theme.palette.categoria.plus1;
    color = '#000';
  } else if (estadoJugador === -1) {
    backgroundColor = theme.palette.categoria.minus1;
    color = '#000';
  } else if (estadoJugador === -2) {
    backgroundColor = theme.palette.categoria.minus2;
    color = '#000';
  }

  return {
    backgroundColor,
    color,
    fontSize: '0.65rem',
    fontWeight: 'bold',
    padding: backgroundColor !== 'transparent' ? '2px 6px' : 0,
    borderRadius: '4px',
    marginRight: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
  };
});

export const FichaJugadorPlan = ({
  jugador,
  rol,
  enCampo,
  asignado,
  esFichaje,
  onDragStart,
  draggable,
  codigoCat,
  categoriaEquipo,
  estado,
  onEstadoChange,
  onActionClick,
  isOutdated,
  fuente,
  posicionAbreviada,
  ...props
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClickEstado = (event) => {
    // Si queremos evitar que el drag capture el click, paramos la propagación
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectEstado = (nuevoEstado) => {
    if (onEstadoChange) {
      onEstadoChange(jugador.idJugador, nuevoEstado);
    }
    handleClose();
  };

  const getIconoEstado = () => {
    switch (estado) {
      case 'Confirmado':
        return <CheckCircleIcon sx={{ fontSize: '1rem', color: theme.palette.success.dark }} />;
      case 'Posible':
        return <HelpOutlineIcon sx={{ fontSize: '1rem', color: theme.palette.info.main }} />;
      case 'En duda':
        return <HelpOutlineIcon sx={{ fontSize: '1rem', color: theme.palette.warning.main }} />;
      case 'Difícil':
        return <BlockIcon sx={{ fontSize: '1rem', color: theme.palette.error.main }} />;
      default:
        return <HelpOutlineIcon sx={{ fontSize: '1rem', color: theme.palette.warning.main }} />;
    }
  };

  const estadoCategoriaJugador =
    codigoCat && categoriaEquipo ? obtenerEstadoJugadorEnCategoria(codigoCat, categoriaEquipo) : 0;

  return (
    <FichaJugadorPlanWrapper
      rol={rol}
      enCampo={enCampo}
      asignado={asignado}
      esFichaje={esFichaje}
      draggable={draggable && !asignado}
      onDragStart={onDragStart}
      fuente={fuente}
      {...props}
    >
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Nombre y categoría */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          {codigoCat && <BadgeCategoria estadoJugador={estadoCategoriaJugador}>{codigoCat}</BadgeCategoria>}
          <Typography
            sx={{
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 'normal',
              color: theme.palette.text.primary,
            }}
          >
            {jugador.nombreJugador}
          </Typography>
          {isOutdated && (
            <Tooltip title="El jugador superará el límite de edad para esta categoría" arrow>
              <WarningAmberIcon
                sx={{ fontSize: '0.9rem', color: theme.palette.warning.main, ml: 0.5, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Parte derecha de la ficha: Estado, Posición y Botón Eliminar */}
        <Box sx={{ width: 'auto', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.25 }}>
          {/* Posición */}
          {!enCampo && posicionAbreviada && (
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 'bold',
                color: theme.palette.text.secondary,
                mr: 0.5,
              }}
            >
              {posicionAbreviada}
            </Typography>
          )}

          {/* Estado */}
          <Tooltip title={`Estado: ${estado || 'Confirmado'}`} arrow>
            <IconButton
              size="small"
              onClick={handleClickEstado}
              onMouseDown={(e) => e.stopPropagation()} // Prevenir drag
              sx={{ p: 0.25 }}
            >
              {getIconoEstado()}
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <MenuItem onClick={() => handleSelectEstado('Confirmado')} sx={{ fontSize: '0.8rem' }}>
              <CheckCircleIcon sx={{ fontSize: '1rem', mr: 1, color: theme.palette.success.dark }} /> Confirmado
            </MenuItem>
            <MenuItem onClick={() => handleSelectEstado('Posible')} sx={{ fontSize: '0.8rem' }}>
              <HelpOutlineIcon sx={{ fontSize: '1rem', mr: 1, color: theme.palette.info.main }} /> Posible
            </MenuItem>
            <MenuItem onClick={() => handleSelectEstado('En duda')} sx={{ fontSize: '0.8rem' }}>
              <HelpOutlineIcon sx={{ fontSize: '1rem', mr: 1, color: theme.palette.warning.main }} /> En duda
            </MenuItem>
            <MenuItem onClick={() => handleSelectEstado('Difícil')} sx={{ fontSize: '0.8rem' }}>
              <BlockIcon sx={{ fontSize: '1rem', mr: 1, color: theme.palette.error.main }} /> Difícil
            </MenuItem>
          </Menu>

          {/* Ver Ficha */}
          <Tooltip title="Ver ficha del jugador" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/jugador/${jugador.idJugador}`);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ p: 0.25, ml: 0.25, color: theme.palette.text.secondary }}
            >
              <PortraitIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>

          {/* Botón Acción */}
          {(!asignado || enCampo) && (
            <Tooltip title={enCampo ? 'Quitar del campo' : 'Eliminar jugador'} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onActionClick) {
                    onActionClick(jugador, enCampo);
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{ p: 0.25, ml: 0.25, color: enCampo ? theme.palette.text.secondary : theme.palette.error.main }}
              >
                {enCampo ? <CloseIcon sx={{ fontSize: '1rem' }} /> : <DeleteIcon sx={{ fontSize: '1rem' }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </FichaJugadorPlanWrapper>
  );
};

export default FichaJugadorPlan;
